import { useState, useEffect } from 'react';
import { Coins, ShoppingCart } from 'lucide-react';
import { API_URL } from '../../config/api';

const DAYS = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
const HOURS = Array.from({ length: 14 }, (_, i) => i + 9); // 09:00 - 22:00

const SalesHeatmap = ({ getDateRange, reportStoreFilter, formatCurrency }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('count'); // 'count' or 'revenue'

  useEffect(() => {
    const fetch_ = async () => {
      setLoading(true);
      try {
        const dr = getDateRange();
        let params = `start_date=${dr.start}&end_date=${dr.end}`;
        if (reportStoreFilter === 'gold') params += '&store_id=1';
        else if (reportStoreFilter === 'silver') params += '&store_ids=2,3';
        else if (reportStoreFilter !== 'all') params += `&store_id=${reportStoreFilter}`;

        const res = await fetch(`${API_URL}/reports/sales-heatmap?${params}`);
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch_();
  }, [getDateRange, reportStoreFilter]);

  if (loading) return <div className="empty-state"><div className="empty-icon">⏳</div><p>Yoğunluk haritası hazırlanıyor...</p></div>;

  // Build matrix: DAYOFWEEK MySQL returns 1=Sunday, 2=Monday, ... 7=Saturday
  const matrix = {};
  let maxVal = 0;
  data.forEach(r => {
    const key = `${r.day_of_week}-${r.hour_of_day}`;
    const val = mode === 'count' ? r.sale_count : parseFloat(r.total_revenue) || 0;
    matrix[key] = val;
    if (val > maxVal) maxVal = val;
  });

  const totalSales = data.reduce((s, r) => s + (r.sale_count || 0), 0);
  const totalRevenue = data.reduce((s, r) => s + (parseFloat(r.total_revenue) || 0), 0);

  // En yoğun saat/gün
  let peakDay = '', peakHour = '';
  if (data.length > 0) {
    const dayTotals = {};
    const hourTotals = {};
    data.forEach(r => {
      dayTotals[r.day_of_week] = (dayTotals[r.day_of_week] || 0) + r.sale_count;
      hourTotals[r.hour_of_day] = (hourTotals[r.hour_of_day] || 0) + r.sale_count;
    });
    const topDay = Object.entries(dayTotals).sort((a, b) => b[1] - a[1])[0];
    const topHour = Object.entries(hourTotals).sort((a, b) => b[1] - a[1])[0];
    if (topDay) peakDay = DAYS[parseInt(topDay[0]) - 1] || '';
    if (topHour) peakHour = `${topHour[0]}:00`;
  }

  const getColor = (val) => {
    if (!val || maxVal === 0) return 'rgba(15,118,110,0.03)';
    const intensity = val / maxVal;
    if (intensity > 0.8) return 'rgba(15,118,110,0.9)';
    if (intensity > 0.6) return 'rgba(15,118,110,0.7)';
    if (intensity > 0.4) return 'rgba(15,118,110,0.5)';
    if (intensity > 0.2) return 'rgba(15,118,110,0.3)';
    return 'rgba(15,118,110,0.12)';
  };

  // Reorder: Monday first (MySQL DAYOFWEEK: 2=Mon, 3=Tue, 4=Wed, 5=Thu, 6=Fri, 7=Sat, 1=Sun)
  const dayOrder = [2, 3, 4, 5, 6, 7, 1];

  return (
    <div className="tab-content">
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon"><ShoppingCart size={20} /></div>
          <div className="stat-info">
            <div className="stat-value">{totalSales}</div>
            <div className="stat-label">Toplam Satış</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><Coins size={20} style={{ color: '#0f766e' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{formatCurrency(totalRevenue)}</div>
            <div className="stat-label">Toplam Gelir</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ fontSize: 20 }}>📅</div>
          <div className="stat-info">
            <div className="stat-value">{peakDay || '-'}</div>
            <div className="stat-label">En Yoğun Gün</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ fontSize: 20 }}>⏰</div>
          <div className="stat-info">
            <div className="stat-value">{peakHour || '-'}</div>
            <div className="stat-label">En Yoğun Saat</div>
          </div>
        </div>
      </div>

      <div className="chart-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Satış Yoğunluk Haritası</h3>
          <div style={{ display: 'flex', gap: 4 }}>
            <button className={`period-btn ${mode === 'count' ? 'active' : ''}`} onClick={() => setMode('count')}>Adet</button>
            <button className={`period-btn ${mode === 'revenue' ? 'active' : ''}`} onClick={() => setMode('revenue')}>Ciro</button>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: `100px repeat(${HOURS.length}, 1fr)`, gap: 2, minWidth: 700 }}>
            {/* Header */}
            <div style={{ fontSize: 11, color: '#5a6c7d', padding: '4px 0' }} />
            {HOURS.map(h => (
              <div key={h} style={{ fontSize: 11, color: '#5a6c7d', textAlign: 'center', padding: '4px 0' }}>{h}:00</div>
            ))}

            {/* Rows */}
            {dayOrder.map(dayIdx => (
              <>
                <div key={`label-${dayIdx}`} style={{ fontSize: 12, fontWeight: 600, color: '#2c3e50', display: 'flex', alignItems: 'center', paddingRight: 8 }}>
                  {DAYS[dayIdx - 1]}
                </div>
                {HOURS.map(h => {
                  const val = matrix[`${dayIdx}-${h}`] || 0;
                  return (
                    <div
                      key={`${dayIdx}-${h}`}
                      title={`${DAYS[dayIdx - 1]} ${h}:00 - ${mode === 'count' ? `${val} satış` : formatCurrency(val)}`}
                      style={{
                        background: getColor(val),
                        borderRadius: 3,
                        minHeight: 36,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: val > 0 ? 600 : 400,
                        color: val / maxVal > 0.5 ? '#fff' : '#2c3e50',
                        cursor: 'default',
                        transition: 'background 0.2s',
                      }}
                    >
                      {val > 0 ? (mode === 'count' ? val : `₺${Math.round(val / 1000)}k`) : ''}
                    </div>
                  );
                })}
              </>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, justifyContent: 'center' }}>
          <span style={{ fontSize: 11, color: '#5a6c7d' }}>Az</span>
          {[0.03, 0.12, 0.3, 0.5, 0.7, 0.9].map((op, i) => (
            <div key={i} style={{ width: 24, height: 14, background: `rgba(15,118,110,${op})`, borderRadius: 2 }} />
          ))}
          <span style={{ fontSize: 11, color: '#5a6c7d' }}>Çok</span>
        </div>
      </div>
    </div>
  );
};

export default SalesHeatmap;
